import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from './Toolkit/Table';
import MButton from '../Toolkit/Components/Controls/Button';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {IMsalContext} from '@azure/msal-react';
import {JobAssignment, JobAssignmentReply, JobData, JobDataMultiReply} from '../Api/ControllerTypes/JobAssignmentTypes';
import {AddAdditionalWorkRequest, AddJobTaskRequest, JobAssignmentClient, UpdateJobAssignmentRequest, UpdateJobTmTypeRequest} from '../Api/JobAssignmentClient';
import MDropDown from '../Toolkit/Components/Controls/DropDown';
import {MNullable} from '../Toolkit/Utils';
import NoteDialog from '../Dialogs/NoteDialog';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import AdditionalWorkDialog, {WorkItemData} from '../Dialogs/AdditionalWorkDialog';
import MessageDialog from '../Dialogs/MessageDialog';
import EnumContext from '../Context/enum-context';
import AddTaskDialog, {TaskData} from '../Dialogs/AddTaskDialog';
import MDropDownMulti from './Toolkit/DropDownMulti';

type JobAssignmentRow = {
    data: JobData;
    dateString: string;
}

type Props = {
    parentId: string;
    msalContext: IMsalContext;
    selectCrew: () => void;
    selectDay: (date: Date, tmType: number[]) => void;
    jobStatus: number;
}

/** */
type State = {
    filters: MTableFilters;
    rowData: JobAssignmentRow[];
    selectedRow?: JobAssignmentRow;
}

class JobCrewsControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_jobClient: NSwagAdapter<JobAssignmentClient>;
    private readonly m_editNoteRef: RefObject<NoteDialog>;
    private readonly m_addWorkRef: RefObject<AdditionalWorkDialog>;
    private readonly m_addTaskRef: RefObject<AddTaskDialog>;
    private readonly m_messageDialogRef: RefObject<MessageDialog>;

    private m_noteEditId: number = 0;

    public constructor(props: Props) {
        super(props);

        this.m_editNoteRef = createRef<NoteDialog>();
        this.m_addWorkRef = createRef<AdditionalWorkDialog>();
        this.m_addTaskRef = createRef<AddTaskDialog>();
        this.m_messageDialogRef = createRef<MessageDialog>();
        this.m_jobClient = new NSwagAdapter<JobAssignmentClient>(new JobAssignmentClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.selectCrew_Click = this.selectCrew_Click.bind(this);
        this.editNote_Click = this.editNote_Click.bind(this);
        this.addAdditionalWork_Click = this.addAdditionalWork_Click.bind(this);
        this.addJobTask_Click = this.addJobTask_Click.bind(this);
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.isNoRowSelected = this.isNoRowSelected.bind(this);
        this.assignTemplate = this.assignTemplate.bind(this);
        this.editTemplate = this.editTemplate.bind(this);
        this.tmUsageTemplate = this.tmUsageTemplate.bind(this);
        this.tmTypeTemplate = this.tmTypeTemplate.bind(this);
        this.isAssignDisabled = this.isAssignDisabled.bind(this);
        this.isNoteEditDisabled = this.isNoteEditDisabled.bind(this);
        this.isAdditionalWorkDisabled = this.isAdditionalWorkDisabled.bind(this);
        this.isJobTaskDisabled = this.isJobTaskDisabled.bind(this);
        this.onNoteEdit = this.onNoteEdit.bind(this);
        this.onAddWork = this.onAddWork.bind(this);
        this.onAddTask = this.onAddTask.bind(this);
    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: [],
            selectedRow: undefined,
        };
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private isNoRowSelected(): boolean {
        return this.state.selectedRow === undefined;
    }

    private isAssignDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access ||
            this.props.jobStatus === 9 // Cancelled;
    }

    private isAdditionalWorkDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access ||
            this.props.jobStatus === 9 // Cancelled;
    }

    private isJobTaskDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access ||
            this.props.jobStatus === 9 // Cancelled;
    }

    private isNoteEditDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access ||
            this.props.jobStatus === 9 // Cancelled;
    }

    private async selectCrew_Click(): Promise<void> {
        this.props.selectCrew();
    }

    private addAdditionalWork_Click(): void {
        this.m_addWorkRef.current!.show(this.onAddWork);
    }

    private addJobTask_Click(): void {
        this.m_addTaskRef.current!.show(this.onAddTask);
    }

    private async selectCrewDay_Click(lineData: JobAssignment): Promise<void> {
        this.props.selectDay(lineData.assignedDate!, lineData.tmType);
    }

    private async editNote_Click(lineData: JobAssignment): Promise<void> {
        this.m_noteEditId = lineData.id;
        this.m_editNoteRef.current!.show('Edit Note', false, this.onNoteEdit, 255, lineData.tmUsageNote);
    }

    private table_onSelectionChange(selection: JobAssignmentRow | JobAssignmentRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as JobAssignmentRow | undefined);

    }

    private async loadPageData(): Promise<void> {

        //Read job data.
        const jobResult = await this.m_jobClient.callWithReply(
            JobDataMultiReply, (client) => client.getAll(undefined, undefined, this.props.parentId));
        jobResult.throwIfFailed();

        this.safeSetState(draft => {
            draft.rowData = jobResult.reply!.jobAssignments.map(n => ({
                data: n,
                dateString: n.jobAssignment.assignedDate?.toDateString() ?? ''
            }))
        });
    }

    private async onAddWork(newWork: WorkItemData): Promise<void> {
        const editResult = await this.m_jobClient.callWithReply(
            JobAssignmentReply, (client) => client.addAdditionalWork(
                new AddAdditionalWorkRequest({
                    requestId: this.props.parentId,
                    tmType: newWork.tmType,
                    workItemDate: newWork.workDate,
                    itemNote: newWork.noteText
                })
            ));
        editResult.throwIfFailed();

        await this.loadPageData();
    }

    private async onAddTask(newWork: TaskData): Promise<void> {
        const editResult = await this.m_jobClient.callWithReply(
            JobAssignmentReply, (client) => client.addTask(
                new AddJobTaskRequest({
                    requestId: this.props.parentId,
                    requiredTasks: newWork.tmType,
                })
            ));
        editResult.throwIfFailed();

        await this.loadPageData();
    }

    private async onNoteEdit(newNote : string): Promise<void> {
        const editResult = await this.m_jobClient.callWithReply(
            JobAssignmentReply, (client) => client.updateNote(
                this.m_noteEditId,
                newNote
            ));
        editResult.throwIfFailed();

        await this.loadPageData();
    }

    private dateTemplate(rowData: JobAssignmentRow): string | undefined {
        if(rowData.data.jobAssignment.assignedDate !== null) {
            try {
                return rowData.data.jobAssignment.assignedDate.toLocaleDateString('en-GB', {weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'});
            } catch {
                return '';
            }
        }
        return '';
    }

    private assignTemplate(rowData: JobAssignmentRow): ReactElement {
        return <MButton label="Assign"
                        color="primary"
                        disabled={this.isAssignDisabled}
                        onClick={this.selectCrewDay_Click.bind(this, rowData.data.jobAssignment)}/>;
    }

    private editTemplate(rowData: JobAssignmentRow): ReactElement {
        return <MButton label="Edit"
                        color="primary"
                        disabled={this.isNoteEditDisabled}
                        onClick={this.editNote_Click.bind(this, rowData.data.jobAssignment)}/>;
    }

    private crewTemplate(rowData: JobAssignmentRow) : ReactElement {
        const crewName = rowData.data.crews !== null && rowData.data.crews!.length > 0
            ? rowData.data.crews?.map(c => c?.crewName ?? '').sort().join(', ')
            : '';

        return (<span style={{whiteSpace: 'normal'}}>
            { crewName }
        </span>);
    }

    private tmUsageTemplate(rowData: JobAssignmentRow) : ReactElement {
        return (<span>
            <MDropDown id={'tmUsage' + rowData.data.jobAssignment.id}
                       items={this.context?.enums === null ? [] : this.context?.enums.tmUsage.map((enumvalue) => {
                               return {label: enumvalue.name, value: enumvalue.id};
                           })}
                       disabled={this.isNoteEditDisabled}
                       initialValue={rowData.data.jobAssignment.tmUsage}
                       onChange={this.usage_Change.bind(this, rowData)}
            />
        </span>);
    }

    private async usage_Change(rowData: JobAssignmentRow, newValue: MNullable<number>) {
        const updateRequest = new UpdateJobAssignmentRequest({
            id: rowData.data.jobAssignment.id,
            tmUsage: newValue!
        });
        const updateResult = await this.m_jobClient.callWithReply(
            JobAssignmentReply, (client) => client.updateUsage(updateRequest)
        );
        updateResult.throwIfFailed();
    }

    private tmTypeTemplate(rowData: JobAssignmentRow) : ReactElement {
        return <span>
            <MDropDownMulti<number> id={'actualTmTypes'}
                                    disabled={this.isNoteEditDisabled}
                                    items={this.context?.enums === null ? [] : this.context?.enums.jobTypes.map((enumvalue) => {
                                        return {label: enumvalue.name, value: enumvalue.id};
                                    })}
                                    initialValue={rowData.data.jobAssignment.tmType}
                                    onChange={this.tmType_Change.bind(this, rowData)}
                                    maxSelectedLabels={2}
            />
        </span>;
    }

    private async tmType_Change(rowData: JobAssignmentRow,  newValue: number[]) {
        const updateRequest = new UpdateJobTmTypeRequest({
            id: rowData.data.jobAssignment.id,
            tmTypes: newValue!
        });
        const updateResult = await this.m_jobClient.callWithReply(
            JobAssignmentReply, (client) => client.updateTmType(updateRequest)
        );
        updateResult.throwIfFailed();
    }

    private tmNoteTemplate(rowData: JobAssignmentRow) {
        const noteStyle = rowData.data.jobAssignment.additionalWork
                          ? {border: "solid", borderWidth: "1px", borderColor: "#689F38", padding: "8px 8px"}
                          : {};

        return <span style={{fontSize: 'small', whiteSpace: 'normal'}}>
            <div style={noteStyle}>
                {rowData.data.jobAssignment.tmUsageNote}
            </div>
        </span>;
    }

    public async refresh(): Promise<void> {
        await this.loadPageData();
    }

    public render(): ReactElement {
        return (
            <React.Fragment>
                <MToolbar>
                    <MButton label="Assign Crew"
                             color="primary"
                             icon={'pi pi-users'}
                             disabled={this.isAssignDisabled}
                             onClick={this.selectCrew_Click}/>
                    <MSpacer/>
                    <MButton label="Add Additional Work"
                             color="success"
                             icon={'pi pi-plus'}
                             disabled={this.isAdditionalWorkDisabled}
                             onClick={this.addAdditionalWork_Click}/>
                    <MSpacer/>
                    <MButton label="Add Scheduled Task"
                             color="success"
                             icon={'pi pi-plus'}
                             disabled={this.isJobTaskDisabled}
                             onClick={this.addJobTask_Click}/>
                </MToolbar>

                <NoteDialog ref={this.m_editNoteRef}/>
                <AdditionalWorkDialog ref={this.m_addWorkRef}
                                      tmTypes={this.context.enums?.jobTypes ?? []}
                />
                <AddTaskDialog ref={this.m_addTaskRef}
                               tmTypes={this.context.enums?.jobTypes ?? []}
                />
                <MessageDialog ref={this.m_messageDialogRef}/>

                <MTable
                    rowKey="data.jobAssignment.id"
                    rowData={this.state.rowData}
                    filters={this.state.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    showSelection={false}
                    sortField={'data.jobAssignment.assignedDate'}
                    sortOrder={1}
                    rowGroupMode="rowspan"
                    groupRowsBy="dateString"
                >
                    <Column field="dateString"
                            header="Date"
                            body={this.dateTemplate}
                            dataType="date"
                            filter={false}
                            sortable={false}
                            style={{width: '10%'}}/>
                    <Column header="TM Type"
                            body={this.tmTypeTemplate}
                            field="data.jobAssignment.tmType"
                            style={{width: '10%'}}/>
                    <Column header="Assign"
                            body={this.assignTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '5%'}}/>
                    <Column field="data.crews"
                            header="Crews"
                            body={this.crewTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '20%'}}/>
                    <Column header="TM Usage"
                            body={this.tmUsageTemplate}
                            field="data.jobAssignment.tmUsage"
                            style={{width: '10%'}}/>
                    <Column header="Note"
                            body={this.editTemplate}
                            filter={false}
                            sortable={false}
                            style={{width: '5%'}}/>
                    <Column header=""
                            body={this.tmNoteTemplate}
                            field="data.jobAssignment.tmUsageNote"
                            style={{width: '40%'}}/>
                </MTable>
            </React.Fragment>
        );
    }
}

export default JobCrewsControl;